import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import DataCard from 'anf-core-react/components/DataCard';
import Disclosure from 'anf-core-react/components/Disclosure';

import { formatDayOfWeek, formatHours, getDropOffLocationImage, getDropOffLocationName } from '../../utils/drop-off-location-helper';
import TmntText from '../tmnt-text/tmnt-text';

const dropOffLocationProp = PropTypes.shape({
  address: PropTypes.shape({
    city: PropTypes.string,
    postalCode: PropTypes.string,
    state: PropTypes.string,
    street: PropTypes.string,
  }),
  distance: PropTypes.number.isRequired,
  geoPosition: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  locationId: PropTypes.string,
  name: PropTypes.string.isRequired,
  operatingHours: PropTypes.arrayOf(
    PropTypes.shape({
      dayOfWeek: PropTypes.string,
      closeHours: PropTypes.string,
      openHours: PropTypes.string,
    }),
  ),
  providerType: PropTypes.string.isRequired,
});

export default function DropOffLocationsDisplay({
  location,
  idx,
  variant,
}) {
  const [showHours, setShowHours] = useState(false);
  const storeHoursText = TmntText({ k: 'STOREHOURS', textOnly: true });

  const operatingHoursDisplay = useMemo(() => {
    const items = location?.operatingHours?.map((hoursObj) => {
      if (!hoursObj.openHours || !hoursObj.closeHours) return null;

      return (
        <tr key={hoursObj.dayOfWeek}>
          <td className="day-of-week">{formatDayOfWeek(hoursObj.dayOfWeek)}</td>
          <td>{`${formatHours(hoursObj.openHours)} - ${formatHours(hoursObj.closeHours)}`}</td>
        </tr>
      );
    });

    const filteredItems = items?.filter((item) => item !== null);

    if (!filteredItems.length) return null;

    return filteredItems;
  }, [location]);

  return (
    <div
      className={`drop-off-location-wrapper ${idx === 0 ? 'border-bottom' : ''}`}
    >
      <img
        alt="Dropoff Location Logo"
        className={`dropoff-location-logo ${getDropOffLocationName(location)}`}
        src={getDropOffLocationImage(location)}
      />
      <DataCard
        headerTitle={location.name}
        variant={variant || 'bordered'}
      >
        <div className="street-wrapper">{location.address?.street}</div>
        <div className="city-state-wrapper">{`${location.address?.city}, ${location.address?.state}`}</div>
        <div className="distance-wrapper">
          {location.distance}
          &nbsp;
          miles
        </div>
        {operatingHoursDisplay && (
          <div className="disclosure-wrapper">
            <Disclosure
              id={`storeHours-${idx}`}
              label={storeHoursText}
              onClick={() => setShowHours(!showHours)}
              isExpanded={showHours}
            >
              <table className="store-hours" role="presentation">
                <tbody>
                  {operatingHoursDisplay}
                </tbody>
              </table>
            </Disclosure>
          </div>
        )}
      </DataCard>
    </div>
  );
}

DropOffLocationsDisplay.propTypes = {
  location: dropOffLocationProp,
  idx: PropTypes.number.isRequired,
  variant: PropTypes.string,
};
